import { motion } from 'framer-motion';
import { Shield, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ROUTE_PATHS } from '@/lib/index';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <div className="bg-charcoal text-charcoal-foreground py-20">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-2 text-primary text-sm mb-4">
              <Link to={ROUTE_PATHS.HOME} className="hover:text-primary/80 transition-colors">Home</Link>
              <ChevronRight size={14} />
              <span>Privacy Policy</span>
            </div>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                <Shield className="text-primary" size={24} />
              </div>
              <span className="text-primary text-xs font-semibold tracking-widest uppercase">Legal Document I / III</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Privacy Policy</h1>
            <p className="text-charcoal-foreground/70 text-lg">
              SHONCHIN MANOR &amp; CO. — How we collect, use and protect your personal information.
            </p>
            <p className="text-charcoal-foreground/50 text-sm mt-3">Effective date: 2026 · © 2026 SHONCHIN MANOR &amp; CO.</p>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="prose prose-neutral max-w-none"
        >
          <div className="bg-card border border-border rounded-2xl p-8 mb-8 text-sm text-muted-foreground leading-relaxed">
            SHONCHIN MANOR &amp; CO. ("the Company", "we" or "us") is a professional firm registered in Dubai providing strategic and management consulting services to international clients. This Privacy Policy explains how we collect, use, retain and protect your personal information, and your rights in respect of it.
          </div>

          <Section number="1" title="Scope">
            <p>This Policy applies to personal information you provide, or that we collect, when you visit our website, send us an enquiry, or otherwise communicate with us. It does not apply to third-party websites linked from our site, which are governed by their own privacy policies.</p>
          </Section>

          <Section number="2" title="Information We Collect">
            <p>We collect only the basic information necessary to interact with you and to operate our website, namely:</p>
            <ul>
              <li><strong>Information you provide:</strong> your name, email address, organisation (if applicable) and the content of any message you submit through our contact form.</li>
              <li><strong>Information collected automatically:</strong> IP address, browser type, operating system, access time and pages viewed.</li>
              <li>Information collected via cookies and similar technologies (see Section 4).</li>
            </ul>
            <p>We do not actively collect sensitive personal information (such as identity documents, financial accounts or health information) unless strictly necessary, and in such cases we will obtain your separate consent.</p>
          </Section>

          <Section number="3" title="How We Use Your Information">
            <p>We use your personal information for the following purposes:</p>
            <ul>
              <li>to respond to your enquiry and establish business contact;</li>
              <li>to provide the information or services you have requested;</li>
              <li>to maintain the security and proper operation of our website;</li>
              <li>to improve the quality of our services and user experience;</li>
              <li>to comply with legal obligations applicable to us.</li>
            </ul>
            <p>We undertake that we will not sell your personal information to any third party, and will not use it for purposes beyond those stated in this Policy, unless with your further consent or as required by law.</p>
          </Section>

          <Section number="4" title="Cookies and Similar Technologies">
            <p>Our website uses a limited set of essential cookies to maintain basic functionality, and may use anonymised analytics cookies to understand usage patterns. You may manage or disable cookies through your browser settings, though some features may be affected.</p>
          </Section>

          <Section number="5" title="Sharing and Disclosure">
            <p>We share your personal information with third parties only in the following limited circumstances:</p>
            <ul>
              <li>service providers operating our website or providing necessary services on our behalf (e.g., hosting, email, analytics), under binding confidentiality and data-security obligations;</li>
              <li>where disclosure is required by applicable law, court order or lawful request of a competent authority;</li>
              <li>where necessary to protect the vital and lawful interests of us, you or any other person;</li>
              <li>in the event of corporate reorganisation, merger or sale, provided that the recipient continues to be bound by this Policy.</li>
            </ul>
          </Section>

          <Section number="6" title="International Data Transfers">
            <p>As we serve international clients, your personal information may be transferred outside the United Arab Emirates or processed by service providers located abroad. We will ensure any such transfer complies with UAE law and other applicable law, and put in place reasonable contractual and technical measures to safeguard your information.</p>
            <LegalRef>UAE Federal Decree-Law No. 45 of 2021 on Personal Data Protection (the "PDPL", in force since 2 January 2022); Articles 44–50 of the EU General Data Protection Regulation ("GDPR"); and the California Consumer Privacy Act as amended by CPRA.</LegalRef>
          </Section>

          <Section number="7" title="Storage and Security">
            <p>We apply reasonable technical and organisational measures in line with industry practice — including transport-layer encryption, access controls and confidentiality obligations on staff — to protect your personal information against unauthorised access, disclosure, alteration or destruction. No method of transmission or storage over the internet can be absolutely secure.</p>
            <p>In the event of a personal data incident likely to affect your rights, we will take prompt remedial action and notify you by appropriate means, in accordance with applicable law.</p>
          </Section>

          <Section number="8" title="Retention">
            <p>We retain your personal information only for as long as is necessary to fulfil the purposes set out in this Policy. As a general matter, contact-form submissions are retained for up to thirty-six (36) months from the last interaction, and server logs for up to twelve (12) months, unless a longer period is required by law. Upon expiry, information is deleted or anonymised.</p>
          </Section>

          <Section number="9" title="Your Rights">
            <p>Subject to applicable law, you have the following rights in respect of your personal information:</p>
            <ul>
              <li>to be informed and to access a copy of the personal information we hold about you;</li>
              <li>to have inaccurate or incomplete information corrected or completed;</li>
              <li>to have your information erased in the circumstances provided by law;</li>
              <li>to restrict or object to certain processing activities (where the GDPR applies);</li>
              <li>to withdraw any consent previously given (without affecting the lawfulness of processing carried out before withdrawal);</li>
              <li>to lodge a complaint with us or with a competent supervisory authority.</li>
            </ul>
          </Section>

          <Section number="10" title="Children">
            <p>Our website is intended for business entities and adult professionals. We do not knowingly collect personal information from individuals under the age of eighteen (18) or such other age of majority applicable in your jurisdiction. If we become aware that we have done so, we will delete the information promptly.</p>
          </Section>

          <Section number="11" title="Updates">
            <p>We may update this Policy from time to time to reflect changes in our business or in applicable law. Any updated version will be published on our website and, where appropriate, notified by other reasonable means. Please check periodically for the latest version.</p>
          </Section>
        </motion.div>

        {/* Footer nav between legal docs */}
        <div className="mt-16 pt-8 border-t border-border flex flex-col sm:flex-row gap-4 justify-between">
          <Link to={ROUTE_PATHS.TERMS} className="inline-flex items-center gap-2 text-primary hover:underline text-sm font-medium">
            <ChevronRight size={16} /> Terms of Service
          </Link>
          <Link to={ROUTE_PATHS.COMPLIANCE} className="inline-flex items-center gap-2 text-primary hover:underline text-sm font-medium">
            <ChevronRight size={16} /> Compliance Statement
          </Link>
        </div>
      </div>
    </div>
  );
}

function Section({ number, title, children }: { number: string; title: string; children: React.ReactNode }) {
  return (
    <div className="mb-10">
      <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-3">
        <span className="w-8 h-8 rounded-full bg-primary/10 text-primary text-sm font-bold flex items-center justify-center flex-shrink-0">
          {number}
        </span>
        {title}
      </h2>
      <div className="text-muted-foreground leading-relaxed space-y-3 pl-11">
        {children}
      </div>
    </div>
  );
}

function LegalRef({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-4 p-4 bg-primary/5 border-l-4 border-primary rounded-r-lg text-xs text-muted-foreground">
      <span className="font-semibold text-primary uppercase tracking-wide text-xs">Legal Reference — </span>
      {children}
    </div>
  );
}
